import React, {useState} from 'react';
import axios from 'axios';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {
    InputField,
    StyledDialog,
    StyledDialogActions,
    StyledDialogContent,
    StyledDialogTitle
} from './CreateDataSourceDialog.styled';
import {CircularProgress} from '@mui/material';
import FieldHelpSuffix from '../common/FieldHelpSuffix';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {parseError} from "../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {useUser} from "../../contexts/UserContext";
import {ValidationError} from "../../utils/validation";
import {validateRequiredTrimmed} from "../../utils/required-validation";

interface Props {
    open: boolean;
    onClose: () => void;
    onDataSourceCreated: (dataSourceId: number) => void;
}

// TODO: move endpoint to single service dedicated to API
const CreateDataSourceDialog: React.FC<Props> = ({open, onClose, onDataSourceCreated}) => {
    const {t} = useTranslation();
    const {triggerConversationRefresh} = useUser();
    const {showSnackbar} = useSnackbar();
    const [dataSourceName, setDataSourceName] = useState('');
    const [nameTouched, setNameTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<ValidationError | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const nameValidationError: ValidationError | null = validateRequiredTrimmed(dataSourceName, 'createDataSource.nameRequired');
    const visibleError = submitError ?? (((nameTouched || submitted) && nameValidationError) ? nameValidationError : null);

    const handleDataSourceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDataSourceName(e.target.value);
        setSubmitError(null);
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        setSubmitError(null);
        if (nameValidationError) {
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await axios.post('/documents/by-collection', {collectionName: dataSourceName});
            showSnackbar(t('createDataSource.createSuccess'), 'success');
            onDataSourceCreated(response.data.documentCollectionId);
            onClose();
            triggerConversationRefresh();
            setDataSourceName('');
        } catch (error) {
            setSubmitError({key: 'createDataSource.createError'});
            showSnackbar(t('createDataSource.createError'), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose} aria-labelledby="create-data-source-title">
            <StyledDialogTitle id="create-data-source-title">{t('createDataSource.title')}</StyledDialogTitle>
            <StyledDialogContent>
                <InputField
                    label={t('createDataSource.dataSourceName')}
                    variant="outlined"
                    fullWidth
                    value={dataSourceName}
                    onChange={handleDataSourceNameChange}
                    onBlur={() => setNameTouched(true)}
                    error={!!visibleError}
                    helperText={visibleError ? String(t(visibleError.key)) : ""}
                    inputProps={{
                        'aria-label': t('createDataSource.dataSourceName') as string,
                    }}
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix
                                asAdornment
                                title={t('createDataSource.help.dataSourceName')}
                                ariaLabel={t('createDataSource.help.dataSourceName') as string}
                            />
                        ),
                    }}
                />
            </StyledDialogContent>
            <StyledDialogActions>
                <CancelButton onClick={onClose} disabled={isSubmitting}>
                    {t('createDataSource.cancel')}
                </CancelButton>
                <ActionButton
                    onClick={handleSubmit}
                    disabled={isSubmitting || !dataSourceName.trim()}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" aria-hidden={true}/> : t('createDataSource.create')}
                </ActionButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default CreateDataSourceDialog;
